import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Calendar, MapPin, Users, ArrowRight, Sparkles, Crown } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { format, parseISO } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Landing = () => {
  const [featuredEvents, setFeaturedEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchFeaturedEvents();
  }, []);

  const fetchFeaturedEvents = async () => {
    try {
      const response = await axios.get(`${API}/events`);
      setFeaturedEvents(response.data.slice(0, 3));
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryLabel = (value) => {
    const categories = {
      conference: 'Conference',
      workshop: 'Workshop',
      networking: 'Networking Mixer',
      webinar: 'Webinar',
      seminar: 'Seminar',
      training: 'Training Session',
      panel: 'Panel Discussion',
      other: 'Other',
    };
    return categories[value] || value;
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section 
        className="relative min-h-[90vh] flex items-center justify-center"
        style={{
          backgroundImage: 'url(https://images.pexels.com/photos/1029615/pexels-photo-1029615.jpeg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative z-10 text-center px-8 max-w-5xl mx-auto">
          <div className="mb-6 opacity-0 animate-fade-in animate-delay-100">
            <span className="text-3xl md:text-4xl lg:text-5xl tracking-wide" style={{ fontFamily: 'Playfair Display, serif' }}>
              <span className="text-gold font-light italic">AgeForward</span>
              <span className="text-white font-normal ml-2">Society</span>
            </span>
          </div>
          <h1 className="hero-title text-white mb-8 opacity-0 animate-fade-in animate-delay-200">
            Connect. Collaborate. Advance.
          </h1>
          <p className="text-lg md:text-xl text-gray-300 mb-10 max-w-2xl mx-auto opacity-0 animate-fade-in animate-delay-300">
            The premier networking platform for aging care professionals in the heart of New York City.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center opacity-0 animate-fade-in animate-delay-400">
            <Button 
              data-testid="explore-events-btn"
              onClick={() => navigate('/events')}
              className="bg-gold text-primary hover:bg-gold/90 uppercase tracking-widest px-8 py-6 text-sm font-semibold rounded-sm"
            >
              Explore Events
            </Button>
            {!user && (
              <Button 
                data-testid="join-community-btn"
                variant="outline"
                onClick={() => navigate('/register')}
                className="border-2 border-white text-white hover:bg-white hover:text-primary uppercase tracking-widest px-8 py-6 text-sm font-semibold rounded-sm bg-transparent"
              >
                Join Community
              </Button>
            )}
          </div>
        </div>
        <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce">
          <ArrowRight className="w-6 h-6 text-white/50 rotate-90" />
        </div>
      </section>

      {/* Features Section */}
      <section className="section-spacing bg-secondary/30">
        <div className="container-custom max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="overline text-gold mb-4">Why Join Us</p>
            <h2 className="text-3xl md:text-4xl">Elevate Your Network</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6 md:gap-10">
            <div className="feature-card bg-card">
              <Calendar className="w-10 h-10 text-gold mb-6" strokeWidth={1.5} />
              <h3 className="text-xl mb-3">Curated Events</h3>
              <p className="text-muted-foreground">
                Discover thoughtfully organized conferences, workshops, and networking mixers tailored to aging care.
              </p>
            </div>
            <div className="feature-card bg-card">
              <Users className="w-10 h-10 text-gold mb-6" strokeWidth={1.5} />
              <h3 className="text-xl mb-3">Industry Leaders</h3>
              <p className="text-muted-foreground">
                Connect with executives, practitioners, and innovators shaping the future of aging care.
              </p>
            </div>
            <div className="feature-card bg-card">
              <MapPin className="w-10 h-10 text-gold mb-6" strokeWidth={1.5} />
              <h3 className="text-xl mb-3">NYC Focus</h3>
              <p className="text-muted-foreground">
                All events centered in New York City, the epicenter of healthcare innovation and networking.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Membership Section */}
      <section className="section-spacing">
        <div className="container-custom max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="overline text-gold mb-4">Membership</p>
            <h2 className="text-3xl md:text-4xl">Unlock Premium Benefits</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="border border-border/50 p-8 bg-card">
              <div className="flex items-center gap-2 mb-4">
                <Crown className="w-6 h-6 text-gold" />
                <h3 className="text-xl">Attendee Premium</h3>
              </div>
              <p className="text-3xl font-semibold mb-4">$5<span className="text-sm text-muted-foreground">/month</span></p>
              <ul className="space-y-3 text-muted-foreground mb-6">
                <li>• Early access to events (48hr head start)</li>
                <li>• Premium member badge</li>
                <li>• Member directory access</li>
                <li>• Priority event spots</li>
                <li>• Event discount codes</li>
              </ul>
              <Button 
                onClick={() => navigate('/membership')}
                className="w-full bg-gold text-primary hover:bg-gold/90 uppercase tracking-widest rounded-sm"
                data-testid="attendee-premium-btn"
              >
                Learn More
              </Button>
            </div>
            <div className="border-2 border-gold p-8 bg-card relative">
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gold text-primary text-xs px-3 py-1 uppercase tracking-wider">Popular</span>
              <div className="flex items-center gap-2 mb-4">
                <Crown className="w-6 h-6 text-gold" />
                <h3 className="text-xl">Business Premium</h3>
              </div>
              <p className="text-3xl font-semibold mb-4">$15<span className="text-sm text-muted-foreground">/month</span></p>
              <ul className="space-y-3 text-muted-foreground mb-6">
                <li>• Target events by job function</li>
                <li>• Target by company type</li>
                <li>• Exclude specific audiences</li>
                <li>• Featured event placement</li>
                <li>• Advanced analytics</li>
              </ul>
              <Button 
                onClick={() => navigate('/membership')}
                className="w-full bg-gold text-primary hover:bg-gold/90 uppercase tracking-widest rounded-sm"
                data-testid="business-premium-btn"
              >
                Learn More
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Events Section */}
      <section className="section-spacing bg-secondary/30">
        <div className="container-custom max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12">
            <div>
              <p className="overline text-gold mb-4">Upcoming</p>
              <h2 className="text-3xl md:text-4xl">Featured Events</h2>
            </div>
            <Link 
              to="/events" 
              className="text-gold flex items-center gap-2 mt-4 md:mt-0 hover:underline underline-offset-4"
              data-testid="view-all-events-link"
            >
              View All Events <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {loading ? (
            <div className="flex justify-center py-20">
              <div className="spinner" />
            </div>
          ) : featuredEvents.length === 0 ? (
            <div className="text-center py-20 border border-border/50">
              <Sparkles className="w-12 h-12 text-gold mx-auto mb-4" strokeWidth={1.5} />
              <h3 className="text-xl mb-2">No Events Yet</h3>
              <p className="text-muted-foreground mb-6">Be the first to create an event for the community.</p>
              {user ? (
                <Button 
                  onClick={() => navigate('/create-event')}
                  className="bg-gold text-primary hover:bg-gold/90 uppercase tracking-widest px-6 py-3 text-sm font-semibold rounded-sm"
                  data-testid="create-first-event-btn"
                >
                  Create Event
                </Button>
              ) : (
                <Button 
                  onClick={() => navigate('/login')}
                  className="bg-gold text-primary hover:bg-gold/90 uppercase tracking-widest px-6 py-3 text-sm font-semibold rounded-sm"
                  data-testid="login-to-create-btn"
                >
                  Login to Create
                </Button>
              )}
            </div>
          ) : (
            <div className="bento-grid">
              {featuredEvents.map((event, index) => (
                <Link
                  key={event.id}
                  to={`/events/${event.id}`}
                  className="group card-hover border border-border/50 bg-card"
                  data-testid={`event-card-${index}`}
                >
                  <div className="overflow-hidden relative">
                    <img
                      src={event.image_url && !event.image_url.includes('example.com') ? event.image_url : 'https://images.pexels.com/photos/8761648/pexels-photo-8761648.jpeg'}
                      alt={event.title}
                      className="event-card-image w-full transition-transform duration-500 group-hover:scale-105"
                      onError={(e) => { e.target.src = 'https://images.pexels.com/photos/8761648/pexels-photo-8761648.jpeg'; }}
                    />
                    {event.is_paid && (
                      <span className="absolute top-3 right-3 bg-gold text-primary text-xs px-2 py-1 uppercase tracking-wider">
                        ${event.ticket_price}
                      </span>
                    )}
                    {event.is_featured && (
                      <span className="absolute top-3 left-3 bg-primary text-primary-foreground text-xs px-2 py-1 uppercase tracking-wider flex items-center gap-1">
                        <Crown className="w-3 h-3" /> Featured
                      </span>
                    )}
                  </div>
                  <div className="p-6">
                    <span className="badge-category mb-4 inline-block">
                      {getCategoryLabel(event.category)}
                    </span>
                    <h3 className="text-xl mb-2 group-hover:text-gold transition-colors">
                      {event.title}
                    </h3>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground mt-4">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {format(parseISO(event.date), 'MMM d, yyyy')}
                      </span>
                      <span className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {event.location}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section 
        className="relative py-32"
        style={{
          backgroundImage: 'url(https://images.pexels.com/photos/8761525/pexels-photo-8761525.jpeg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative z-10 text-center px-8 max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-4xl text-white mb-6">
            Ready to Move Forward?
          </h2>
          <p className="text-gray-300 mb-8">
            Join our community of aging care professionals and start networking today.
          </p>
          {!user ? (
            <Button 
              onClick={() => navigate('/register')}
              className="bg-gold text-primary hover:bg-gold/90 uppercase tracking-widest px-8 py-6 text-sm font-semibold rounded-sm"
              data-testid="get-started-btn"
            >
              Get Started
            </Button>
          ) : (
            <Button 
              onClick={() => navigate('/events')}
              className="bg-gold text-primary hover:bg-gold/90 uppercase tracking-widest px-8 py-6 text-sm font-semibold rounded-sm"
              data-testid="browse-events-btn"
            >
              Browse Events
            </Button>
          )}
        </div>
      </section>
    </div>
  );
};

export default Landing;
